import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface Order {
  id: string;
  order_number?: string;
  created_at: string;
  status: string;
  total_amount: number;
  buyer_first_name?: string;
  buyer_last_name?: string;
  customer_email?: string;
  florist_id: string;
  store_name?: string;
  delivery_address: string;
}

const OrderManagement = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!user || !isAdmin) {
      navigate("/");
      return;
    }

    const fetchOrders = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("orders")
        .select(`
          id,
          order_number,
          created_at,
          status,
          total_amount,
          customer_id,
          buyer_first_name,
          buyer_last_name,
          customer_email,
          florist_id,
          delivery_address,
          store_name:florist_id!inner(store_name)
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching orders:", error);
        setOrders([]);
      } else if (data) {
        setOrders(data as Order[]);
      } else {
        setOrders([]);
      }
      
      setLoading(false);
    };

    if (!authLoading && user && isAdmin) {
      fetchOrders();
    }
  }, [user, isAdmin, authLoading, navigate]);

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      delivered: "bg-purple-100 text-purple-800",
    };

    return (
      <Badge className={variants[status] || "bg-gray-100"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredOrders = (orders || []).filter(order => 
    (order.id?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
    (order.delivery_address?.toLowerCase() || "").includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout currentPage="Orders">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-[#4A4F41]">Order Management</h1>
          <p className="text-muted-foreground mt-2">
            View and manage all customer orders
          </p>
        </div>

        <div className="flex items-center gap-4 mt-8">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
          </div>
        ) : (
          <div className="border rounded-lg bg-white shadow-sm">
            <Table>
              <TableHeader className="bg-[#F9F8F6]">
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Florist</TableHead>
                  <TableHead>Delivery Address</TableHead>
                  <TableHead>Order ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No orders found matching your search
                    </TableCell>
                  </TableRow>
                )}
                {(filteredOrders || []).map((order) => {
                  let displayAddress = "N/A";
                  if (order.delivery_address) {
                    try {
                      const addr = JSON.parse(order.delivery_address);
                      displayAddress = `${addr.street || ''}${addr.suburb ? ', ' + addr.suburb : ''}${addr.state ? ', ' + addr.state : ''}${addr.postcode ? ' ' + addr.postcode : ''}`.trim();
                      if (displayAddress.startsWith(',')) displayAddress = displayAddress.substring(1).trim();
                      if (displayAddress === "" || displayAddress === ", , ") displayAddress = order.delivery_address; 
                    } catch (e) {
                      displayAddress = order.delivery_address; 
                    }
                  }
                  const customerName = order.buyer_first_name && order.buyer_last_name 
                    ? `${order.buyer_first_name} ${order.buyer_last_name}` 
                    : order.customer_email || 'N/A';

                  return (
                    <TableRow key={order.id}>
                      <TableCell className="font-medium">{order.order_number || 'N/A'}</TableCell>
                      <TableCell>{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell>{getStatusBadge(order.status || 'unknown')}</TableCell>
                      <TableCell>{order.total_amount ? formatCurrency(order.total_amount) : 'N/A'}</TableCell>
                      <TableCell>{customerName}</TableCell>
                      <TableCell>{order.store_name ? order.store_name.store_name : 'N/A'}</TableCell>
                      <TableCell className="max-w-xs truncate" title={displayAddress}>{displayAddress}</TableCell>
                      <TableCell className="text-xs text-gray-500">({order.id ? order.id.slice(0, 8) : 'N/A'}...)</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default OrderManagement;